/**
 * 数値入力欄 (`<input type="number">`) の共通ラッパー。
 *
 * ## 解決する問題
 *
 * `<input type="number" value={someNumber} onChange={(e) => set(Number(e.target.value))} />`
 * という素直な実装では、**既定値 0 を消せない**という不具合が起きる。
 *
 *   1. ユーザーが「0」を選択して Backspace を押す → `e.target.value === ''`
 *   2. `Number('') === 0` なので state は 0 のまま
 *   3. value が 0 で再レンダリングされ、画面上も「0」に戻る = 消せない
 *
 * 「0 を消してから 15 と打ちたい」という自然な操作ができず、
 * 一度「015」のように打つか、全選択して上書きするしかない状態だった (現場指摘)。
 *
 * ## 解決方法
 *
 * 表示用の **文字列 state** を内部で持ち、入力途中の空文字を一時的に許容する。
 *
 *   - `onChange`: 文字列 state を更新し、数値として解釈できる場合のみ親へ通知する。
 *     空文字のときは `blankValue` (既定 0) を親へ渡すため、保存値の型は number のまま。
 *   - `onBlur`: 空文字のままフォーカスを外したら `blankValue` の表示へ戻し、
 *     画面表示と保存値の不一致を解消する。
 *   - 親から渡された `value` が (外部要因で) 変化した場合は表示文字列を同期する。
 *     ただし「入力中の文字列が同じ数値を表す」ケース (例: 表示 `'05'` / 親 `5`) では
 *     上書きせず、ユーザーの入力途中の見た目を壊さない。
 *
 * ## 使い方
 *
 * ```tsx
 * <NumericInput
 *   value={values.broadcast_delay_seconds}
 *   onChange={(n) => handleChange('broadcast_delay_seconds', n)}
 *   min={0}
 *   step={1}
 *   aria-label="放送遅延"
 *   data-testid="broadcast-delay-input"
 * />
 * ```
 *
 * 整数へ丸めたい場合は `normalizeValue` を渡す (例: `clampNonNegativeInt`)。
 */
import { useEffect, useRef, useState } from 'react';

export interface NumericInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
> {
  /** 現在値 (number)。保存対象の値。 */
  value: number;
  /** 値が変わったときに呼ばれる。空欄入力中は `blankValue` が渡される。 */
  onChange: (value: number) => void;
  /**
   * 入力欄が空のときに親へ通知する値。既定は 0。
   * (表示は空欄のままで、blur 時にこの値の表示へ戻る)
   */
  blankValue?: number;
  /**
   * 親へ通知する前に値を正規化する関数。
   * 例: `clampNonNegativeInt` で「0 以上の整数」に丸める。
   * 省略時は解釈した数値をそのまま渡す。
   */
  normalizeValue?: (value: number) => number;
}

/** 0 以上の整数に丸める正規化関数 (秒数・分数などの入力で使う)。 */
export function clampNonNegativeInt(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value));
}

export function NumericInput({
  value,
  onChange,
  blankValue = 0,
  normalizeValue,
  onBlur,
  ...rest
}: NumericInputProps): React.JSX.Element {
  /** 入力欄に表示する文字列。空文字を一時的に許容するのがこの state の目的。 */
  const [text, setText] = useState<string>(() => String(value));

  /**
   * 直近に親へ通知した値。
   * 親からの `value` 変更が「自分の onChange 由来」か「外部要因」かを判別し、
   * 外部要因のときだけ表示文字列を同期するために使う。
   */
  const lastEmittedRef = useRef<number>(value);

  // 親の value が外部要因で変化したら表示文字列を追従させる。
  // 入力中の文字列が同じ数値を表す場合 (例: '' と 0、'05' と 5) は上書きしない。
  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;
    setText(String(value));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const nextText = e.target.value;
    setText(nextText);

    // 空欄 / 数値として未確定な入力 ('-' や '1e' など) は blankValue 扱いにして、
    // 表示は入力途中のまま維持する (ここで表示を書き戻すと 0 が消せなくなる)。
    const parsed = nextText === '' ? blankValue : Number(nextText);
    const resolved = Number.isNaN(parsed) ? blankValue : parsed;
    const normalized = normalizeValue ? normalizeValue(resolved) : resolved;

    lastEmittedRef.current = normalized;
    onChange(normalized);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    // 空欄のままフォーカスが外れたら、実際の保存値 (blankValue) の表示へ戻す。
    if (text === '' || Number.isNaN(Number(text))) {
      setText(String(blankValue));
    }
    onBlur?.(e);
  };

  return <input type="number" value={text} onChange={handleChange} onBlur={handleBlur} {...rest} />;
}
